import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Divider,
  Flex,
  Heading,
  Badge,
  VStack,
} from '@chakra-ui/react';
import type { SidebarProps } from '@/config/layout-config';
import { DUMMY_ACTIONS, DUMMY_TASKS } from './control-panel-dummy-data';
import { ActionsSection } from './actions-section';
import { TasksSection } from './tasks-section';

const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 240;
const MAX_WIDTH = 520;

export const ControlPanelSidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const [actionsCount, setActionsCount] = useState(DUMMY_ACTIONS.length);
  const dragStartX = useRef<number>(0);
  const dragStartWidth = useRef<number>(DEFAULT_WIDTH);

  const hasRunningTask = DUMMY_TASKS.some((t) => t.status === 'running');
  const hasErrorTask = DUMMY_TASKS.some((t) => t.status === 'error');

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const delta = dragStartX.current - e.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
      setSidebarWidth(newWidth);
    },
    []
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleDragHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartWidth.current = sidebarWidth;
    setIsDragging(true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <Box
      w={isCollapsed ? '50px' : `${sidebarWidth}px`}
      minW={isCollapsed ? '50px' : `${MIN_WIDTH}px`}
      h="100%"
      bg="white"
      borderLeft="1px solid"
      borderColor="gray.200"
      position="relative"
      transition={isDragging ? 'none' : 'width 0.3s ease-in-out'}
      display={{ base: 'none', md: 'flex' }}
      flexDirection="column"
      flexShrink={0}
    >
      {/* Drag Handle */}
      {!isCollapsed && (
        <Box
          position="absolute"
          top={0}
          left={0}
          w="6px"
          h="100%"
          cursor="col-resize"
          zIndex={20}
          _hover={{ bg: 'blue.100' }}
          onMouseDown={handleDragHandleMouseDown}
        />
      )}

      {/* Toggle Button */}
      <Box
        position="absolute"
        top="50%"
        left={0}
        transform="translateY(-50%)"
        zIndex={10}
        display={{ base: 'none', md: 'block' }}
      >
        <Box
          as="button"
          aria-label={isCollapsed ? 'Expand control panel' : 'Collapse control panel'}
          onClick={onToggle}
          w="24px"
          h="60px"
          bg="white"
          borderRadius="0 8px 8px 0"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.100' }}
          transition="all 0.2s"
        >
          <Box w="2px" h="24px" bg="gray.400" borderRadius="full" />
        </Box>
      </Box>

      {isCollapsed ? (
        /* Collapsed State */
        <VStack spacing={2} align="center" pt={14} px={1}>
          <Badge
            colorScheme="purple"
            borderRadius="full"
            px={2}
            py={0.5}
            fontSize="xs"
            minW="24px"
            textAlign="center"
          >
            {actionsCount}
          </Badge>
          {hasRunningTask && (
            <Box w="8px" h="8px" borderRadius="full" bg="blue.500" />
          )}
          {hasErrorTask && (
            <Box w="8px" h="8px" borderRadius="full" bg="red.500" />
          )}
        </VStack>
      ) : (
        /* Expanded State */
        <Flex direction="column" h="100%" overflow="hidden" pt={4} pb={4} pl={5} pr={4}>
          <Heading size="md" mb={3} color="gray.800">
            Control Panel
          </Heading>
          <Divider mb={3} />

          <ActionsSection onActionsChange={setActionsCount} />

          <Divider my={3} />

          <TasksSection />
        </Flex>
      )}
    </Box>
  );
};
